import { ChangeDetectorRef, Component, DoCheck, ElementRef, Injector, Input, OnInit, ViewChild } from '@angular/core';

import { AuthenticationService } from '../../services/authentication.service';
import { PermissionService } from '../../services/permission.service';
import { RolePermissionService } from '../../services/role-permission.service';
import { Model } from '../../../api/model';
import { ModalService } from '../../../modals/services/modal.service';
import { ToastService } from '../../../toast/services/toast.service';
import { Collection } from '../../../support/collection/collection';
import { PermissionModel } from '../../models/permission.model';
import { RoleModel } from '@kaiser-software/chassis-core';
import { Observable } from 'rxjs';
import { strReplace } from '../../../support/helpers';

@Component({
    selector: 'chassis-core-role-permission-plugin',
    templateUrl: './role-permission.plugin.html',
    styleUrls: ['./role-permission.plugin.scss'],
    providers: [RolePermissionService]
})
export class RolePermissionPlugin implements OnInit, DoCheck {

    @ViewChild('panel') panel: ElementRef;

    @Input() form; // form handler which processes the main form

    @Input() modelSubject; // the subject to subscribe to in order to check whether the role is loaded or not

    public model: Model;

    public rolePermissions; // the permissions

    /**
     * The AuthenticationService instance.
     */
    protected authenticationService;

    /**
     * The ModalService instance.
     */
    protected modalService;

    /**
     * The ToastService instance.
     */
    protected toasts: ToastService;

    /**
     * The PermissionService instance.
     */
    protected permissionService: PermissionService;

    /**
     * The RolePermissionsService instance.
     */
    protected rolePermissionService: RolePermissionService;

    /**
     * The colums to display in the table
     */
    public tableColumns;

    /**
     * All avaialble permissions
     */
    public permissions;

    /**
     * The permission nodes for the tree component.
     *
     * @type {any[]}
     */
    public permissionNodes = [];

    /**
     * The name used for the sub-fields node
     *
     * @type {string}
     */
    public readonly subFieldsName = 'Fields';

    protected cdr: ChangeDetectorRef;

    public loading = true;

    public error = false;

    public roleDoesNotExist = false;

    constructor(protected injector: Injector) {
        this.authenticationService = this.injector.get(AuthenticationService);
        this.modalService          = this.injector.get(ModalService);
        this.toasts                = this.injector.get(ToastService);
        this.permissionService     = this.injector.get(PermissionService);
        this.rolePermissionService = this.injector.get(RolePermissionService);
        this.cdr                   = this.injector.get(ChangeDetectorRef);
    }

    ngOnInit() {
        this.modelSubject
            .subscribe((model: RoleModel) => {
                if (model.exists()) {
                    this.requestData();
                } else {
                    this.loading          = false;
                    this.roleDoesNotExist = true;
                }
            });

        // this.setupProcessingCallback();

        this.form.success(() => {
            return new Promise((resolve, reject) => {
                this.savePermissions(resolve, reject);
            });
        });
    }

    protected requestData() {
        this.requestPermissions()
            .subscribe((permissions: Collection) => {
                this.permissions = permissions.all();

                this.requestRolePermissions()
                    .subscribe((rolePermissions: Collection) => {
                        this.rolePermissions = rolePermissions.all();

                        this.generatePermissionsTree(permissions);

                        this.markRolePermissionsChecked(permissions, rolePermissions);
                    }, () => {
                        this.error   = true;
                        this.loading = false;
                    }, () => {
                        this.loading = false;
                    });
            }, () => {
                this.error   = true;
                this.loading = false;
            });
    }

    protected requestPermissions() {
        return this.permissionService.all();
    }

    protected requestRolePermissions() {
        return new Observable((observer) => {
            this.modelSubject
                .subscribe((role: RoleModel) => {
                    const parameters = {
                        roleId: role.id
                    };

                    this.rolePermissionService.all(false, parameters)
                        .subscribe((permissions) => {
                            observer.next(permissions)
                        }, (error) => {
                            observer.error(error);
                        }, () => {
                            observer.complete();
                        });
                });
        });
    }

    protected markRolePermissionsChecked(permissions: Collection, rolePermissions: Collection) {
        rolePermissions.forEach((permission: PermissionModel) => {
            const found: PermissionModel = permissions.find('id', permission.id);

            permission.checked = true;
            found.checked      = true;
        });
    }

    // Function that gets executed when the user click save
    public savePermissions(resolve, reject) {
        this.loading = true;

        this.requestRolePermissions()
            .subscribe(() => {
                const data = {
                    'permissions': this.filterCheckedPermissions(
                        new Collection(this.permissions)
                    )
                        .all()
                        .map((item) => {
                            return item.plain();
                        })
                };

                this.modelSubject.subscribe((model: Model) => {
                    const parameters = {roleId: model.id};
                    const request    = this.rolePermissionService.post(data, parameters);

                    request.subscribe(() => {
                        this.loading = false;

                        this.toasts.success('success', 'Permissions saved successfully.', {element: this.panel.nativeElement});

                        resolve();
                    }, (error) => {
                        this.loading = false;

                        this.toasts.error('error', 'Failed to save permissions.', {element: this.panel.nativeElement});

                        reject();
                    });
                });
            });
    }

    protected generatePermissionsTree(permissions) {
        let node;
        let baseStr;
        let children;
        let subFields;
        let subChildren;

        permissions.forEach((permission) => {
            const amountOfDots = permission.name.split('.').length - 1;

            switch (amountOfDots) {
                case 1:
                    // (re)set some variables needed in the process
                    baseStr  = permission.name.split('.', amountOfDots).join('.');
                    children = [];

                    // check if a node with the name already exists
                    if (this.permissionNodes.filter(n => n.name === baseStr).length === 0) {
                        // loop through all permissions to find sub-permissions
                        permissions.forEach((subPermission) => {
                            // if the permission's name is equal to the baseStr and contains the same amount of dots (just read/write)
                            if ((subPermission.name.indexOf(baseStr + '.') >= 0)
                                && ((subPermission.name.split('.').length - 1) === (amountOfDots))) {
                                // create child node
                                // child = subPermission;

                                // push child node to children of parent node
                                children.push(subPermission);
                            }
                        });

                        // create parent node
                        node = {
                            name: baseStr,
                            display_name: this.toDisplayName(baseStr),
                            description: this.toDisplayName(baseStr),
                            children: children
                        };

                        // push parent node to the tree
                        this.permissionNodes.push(node);
                    }
                    break;
                case 2:
                    // (re)set some variables needed in the process
                    baseStr     = permission.name.split('.', amountOfDots).join('.');
                    children    = [];
                    subChildren = [];

                    // check if a node with the name already exists
                    if (this.permissionNodes.filter(n => n.name === baseStr).length === 0) {
                        // loop through all permissions to find sub-permissions
                        permissions.forEach((subPermission) => {
                            // if the permission's name is equal to the baseStr and contains the same amount of dots (just read/write)
                            if ((subPermission.name.indexOf(baseStr + '.') >= 0)
                                && ((subPermission.name.split('.').length - 1) === (amountOfDots))) {
                                // create child node
                                // child = subPermission;

                                // push child node to children of parent node
                                children.push(subPermission);
                            }

                            if ((subPermission.name.indexOf(baseStr + '.') >= 0)
                                && ((subPermission.name.split('.').length - 1) === (amountOfDots + 1))) {
                                // create child node for subfields child node
                                // subChild = subPermission;

                                // push child node to subchildren of subfields child node
                                subChildren.push(subPermission);

                                // check if subfields child node already exists in children
                                if (children.filter(n => n.name === this.subFieldsName).length === 0) {
                                    // create subfields child node
                                    subFields = {
                                        name: this.subFieldsName,
                                        display_name: this.subFieldsName,
                                        description: this.subFieldsName,
                                        children: subChildren
                                    };

                                    // push subfields child node to children of parent node
                                    children.push(subFields);
                                }
                            }
                        });

                        // create parent node
                        node = {
                            name: baseStr,
                            display_name: this.toDisplayName(baseStr),
                            description: this.toDisplayName(baseStr),
                            children: children
                        };

                        // push parent node to tree
                        this.permissionNodes.push(node);
                    }
                    break;
                default:
                    break;
            }
        });
    }

    protected filterCheckedPermissions(permissions: Collection): Collection {
        const checked = permissions.clone([]);

        permissions.forEach((permission: PermissionModel) => {
            if (permission.checked) {
                checked.push(permission);
            }
        });

        return checked;
    }

    ngDoCheck() {
        this.markUncheckedOrCheckedOrPartlyChecked();

        this.cdr.detectChanges();
    }

    protected markUncheckedOrCheckedOrPartlyChecked() {
        this.permissionNodes.forEach((permission) => {
            const status = this.areChildrenAllSelectedOrPartlySelected(permission);

            if (status === 0) {
                permission.checked            = false;
                permission.allChildrenChecked = false;
            } else if (status === 1) {
                permission.checked            = true;
                permission.allChildrenChecked = false;
            } else if (status === 2) {
                permission.checked            = true;
                permission.allChildrenChecked = true;
            }
        });
    }

    protected areChildrenAllSelectedOrPartlySelected(permission): number {
        let checked   = 0;
        let unchecked = 0;

        if (permission.children && permission.children.length) {
            permission.children.forEach((child) => {
                if (child.checked) {
                    if (this.areAllChildrenChecked(child)) {
                        checked++;
                    } else {
                        unchecked++;
                    }
                } else {
                    unchecked++;
                }

                if (checked && unchecked) {
                    // We'll stop unnecessary iterations because we already
                    // know that it's partly checked.
                    return;
                }
            });
        }

        if (checked && unchecked) {
            return 1;
        } else if (checked) {
            return 2;
        } else {
            return 0;
        }
    }

    protected areAllChildrenChecked(permission) {
        if (permission.children && permission.children.length) {
            for (const child of permission.children) {
                if (!child.checked) {
                    return false;
                }

                return this.areAllChildrenChecked(child);
            }
        }

        return true;
    }

    /**
     * If a click event is triggered on a checkbox
     *
     * @param permission
     */
    public toggleCheckbox(permission) {
        if (permission.checked) {
            permission.checked = false;

            this.markChildrenUnchecked(permission);
        } else {
            permission.checked = true;

            this.markChildrenChecked(permission);
        }
    }

    protected markChildrenChecked(permission) {
        this.setChildrenCheckedState(permission, true);
    }

    protected markChildrenUnchecked(permission) {
        this.setChildrenCheckedState(permission, false);
    }

    protected setChildrenCheckedState(permission, checked: boolean) {
        if (permission.children && permission.children.length) {
            permission.children.forEach((child) => {
                child.checked = checked;

                this.setChildrenCheckedState(child, checked);
            });
        }
    }

    protected toDisplayName(value: string) {
        value = strReplace(value, 'entities.', '');
        value = strReplace(value, '-', ' ');
        value = strReplace(value, '_', ' ');
        
        return value;
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/authentication/plugins/role-permission/role-permission.plugin.ts